import { useNavigate } from "react-router-dom";
import { SignDocCategories } from "../Utils/Constants";
import useSignDocTypes from "./useSignDocTypes";

const LEARNING_MANAGEMENT_PATH = '/learningManagement';
const TASKS_PATH = `${LEARNING_MANAGEMENT_PATH}/tasks`;
const CERTIFICATIONS_PATH = `${LEARNING_MANAGEMENT_PATH}/certifications`;
const KNOWLEDGE_CENTER_PATH = `${LEARNING_MANAGEMENT_PATH}/adminKnowledgeCenter`;

/**
 * Custom hook that returns a navigation function for SignDoc-related pages
 *
 * SYSTEMATIC PRIORITY LOGIC:
 * 1. Are we saving from edit page? → Navigate to category tab
 * 2. Does this type NEED content creation? (has redirectUrlOnCreate) → Redirect to edit page
 * 3. FORCE_COMPLETION category? → Redirect to Tasks tab
 * 4. CERTIFICATION category? → Redirect to Certifications tab
 * 5. KNOWLEDGE_CENTER category? → Redirect to Knowledge Center tab
 * 6. Fallback → Tasks tab
 *
 * This ensures types requiring content creation (EXAM, SURVEY, LEARNING_PATH)
 * redirect to their edit pages when created, but navigate to the appropriate category tab when saved.
 *
 * @returns {Function} Navigation function that accepts { signDocType, signDocCategory, signDocId, navigateToTab }
 *
 * @example
 * const navigateAfterSignDocSave = useNavigateAfterSignDocSave();
 * // After saving from edit page - go to Certifications tab
 * navigateAfterSignDocSave({ signDocType: 'EXAM', signDocCategory: 'CERTIFICATION', signDocId: 'exam-123', navigateToTab: true });
 * // After creating - go to edit page for content creation
 * navigateAfterSignDocSave({ signDocType: 'EXAM', signDocCategory: 'CERTIFICATION', signDocId: 'exam-123', navigateToTab: false });
 */
export const useNavigateAfterSignDocSave = () => {
    const navigate = useNavigate();
    const {getRedirectUrlOnCreate} = useSignDocTypes();

    return ({
        signDocType,
        signDocCategory,
        signDocId,
        navigateToTab = false
    }) => {
        let createUrl = getRedirectUrlOnCreate({ type: signDocType, signDocId });

        // If not navigating to tab and type needs content creation, go to edit page
        if (!navigateToTab) {
            if (createUrl) {
                navigate(createUrl, { replace: true });
                return;
            }
        }
        
        // Navigate based on category
        if (signDocCategory === SignDocCategories.FORCE_COMPLETION) {
            // FORCE_COMPLETION types go to Tasks tab
            navigate(TASKS_PATH, { replace: true });
            return;
        }

        if (signDocCategory === SignDocCategories.CERTIFICATION) {
            // CERTIFICATION types go to Certifications tab
            navigate(CERTIFICATIONS_PATH, { replace: true });
            return;
        }

        if (signDocCategory === SignDocCategories.KNOWLEDGE_CENTER) {
            navigate(KNOWLEDGE_CENTER_PATH, { replace: true });
            return;
        }
        
        // Default fallback to Tasks tab
        navigate(TASKS_PATH, { replace: true });
    };
};